import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { formatPlanDisplayName } from '@/features/billing/helpers/subscription-display-helpers';
import { useAccountSignOut } from '@/features/auth/hooks/use-account-sign-out';
import { useWorkspaceSwitch } from '@/features/workspace/hooks/use-workspace-switch';
import {
  WorkspaceSwitchOutcome,
} from '@/contexts/account/types/workspace-switch-outcome-enums';
import { useAccountState } from '@/contexts/account/account-context';
import { useSignupFlowStore } from '@/features/signup/stores/use-signup-flow-store';
import useStore from '@/store/app/use-store';
import type { UseWorkspaceSwitcherReturn } from '../types/use-workspace-switcher-types';

export function useWorkspaceSwitcher(): UseWorkspaceSwitcherReturn {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const { accountsList, currentAccount } = useAccountState();
  const { switchWorkspace, isSwitching } = useWorkspaceSwitch();

  const storedUser = useStore(s => s.user);

  // Sign out with callback to close popover
  const { signOut: doSignOut, isSigningOut } = useAccountSignOut({
    onBeforeSignOut: () => setOpen(false),
  });

  // Sort accounts: active subscriptions first
  const accounts = React.useMemo(() => {
    return [...accountsList].sort((a, b) => {
      const aIsActive = a.subscription?.status === 'active';
      const bIsActive = b.subscription?.status === 'active';
      if (aIsActive === bIsActive) return 0;
      return aIsActive ? -1 : 1;
    });
  }, [accountsList]);

  // Filter accounts by search query (only for large lists)
  const filteredAccounts = React.useMemo(() => {
    if (!searchQuery || accounts.length <= 100) return accounts;
    return accounts.filter(account =>
      account.accountName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [accounts, searchQuery]);

  // User display info
  const user = React.useMemo(() => {
    const email = storedUser?.email;
    const displayName = storedUser?.name?.trim() || email || 'Account';
    // Only include email if it differs from displayName
    const showEmail = email && displayName !== email ? email : undefined;
    return { displayName, email: showEmail };
  }, [storedUser?.email, storedUser?.name]);

  const rawPlanName = currentAccount?.subscription?.subscriptionPlan?.displayName;
  const planGroupName = currentAccount?.subscription?.subscriptionPlanGroup?.displayName;
  const planName = rawPlanName
    ? formatPlanDisplayName(rawPlanName, planGroupName)
    : undefined;

  // Switch workspace and close popover on completion
  const handleSwitchAccount = React.useCallback(
    async (accountId: string) => {
      const result = await switchWorkspace(accountId, {
        userId: storedUser?.sub,
        onRequiresPlan: userId => {
          useSignupFlowStore.getState().setUserId(userId);
        },
      });

      if (result.outcome === WorkspaceSwitchOutcome.NO_OP) {
        setOpen(false);
        return;
      }

      // Close on real completion or failure
      setOpen(false);
    },
    [switchWorkspace, storedUser?.sub]
  );

  // Navigate to add new company flow and close popover
  const handleAddNewCompany = React.useCallback(() => {
    const email = storedUser?.email ?? '';
    void navigate(`/signup/continue?email=${encodeURIComponent(email)}`);
    setOpen(false);
  }, [storedUser?.email, navigate]);

  // Sign out wrapper
  const signOut = React.useCallback(async () => {
    await doSignOut();
  }, [doSignOut]);

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    const openState = nextOpen;
    setOpen(openState);
    if (!openState) {
      setSearchQuery('');
    }
  }, []);

  return {
    // Workspace state
    accounts,
    filteredAccounts,
    currentAccount,
    isLoading: isSwitching,
    searchQuery,
    setSearchQuery,
    handleSwitchAccount,
    handleAddNewCompany,
    planName,

    // User state
    user,

    // Sign out
    signOut,
    isSigningOut,

    // Popover state
    open,
    setOpen: handleOpenChange,
  };
}
